<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <loading :status="loading"/>

        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <h1 class="stage-page-title">{{ $t('Documentation') }}</h1>
                </div>
                <div class="stage-page-header-actions" v-on-clickaway="closeActionDropdowns">
                    <router-link class="btn-stage-icon-plain" :title="$t('Back')" :aria-label="$t('Back')" :to="documentationListPath">
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.chevron-left"></svg-vue>
                    </router-link>
                    <div class="relative inline-flex items-center">
                        <button
                            class="btn-stage-icon-plain"
                            type="button"
                            title="Aktivitätshistorie"
                            aria-label="Aktivitätshistorie"
                            @click="toggleHistoryPopover"
                        >
                            <svg-vue class="h-4 w-4" icon="font-awesome.regular.rectangle-history"></svg-vue>
                        </button>
                        <div
                            v-show="historyPopoverOpen"
                            class="documentation-history-popover origin-top-right absolute top-full right-0 mt-2 w-72 rounded shadow-lg z-50 bg-white border border-ultralight overflow-hidden"
                            style="z-index: 60;"
                        >
                            <div class="stage-icon-menu-header">
                                Änderungshistorie
                            </div>
                            <div class="max-h-80 overflow-y-auto">
                                <template v-if="documentation.change_history && documentation.change_history.length > 0">
                                    <div
                                        v-for="entry in documentation.change_history"
                                        :key="'history-popover-' + entry.id"
                                        class="documentation-history-item px-4 py-2 border-b border-ultralight last:border-b-0"
                                    >
                                        <div class="flex items-center">
                                            <img
                                                :src="entry.user && entry.user.avatar !== 'gravatar' ? entry.user.avatar : (entry.user ? entry.user.gravatar : '')"
                                                alt="Profilbild"
                                                class="h-6 w-6 rounded-full object-cover mr-2"
                                            />
                                            <div class="min-w-0 flex items-center justify-between w-full gap-2">
                                                <div class="text-xs font-medium text-gray-900 truncate">
                                                    {{ entry.user ? entry.user.name : '-' }}
                                                </div>
                                                <div class="text-xs text-gray-400 whitespace-nowrap">
                                                    {{ formatDateTime(entry.created_at) }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <div v-else class="px-4 py-3 text-sm text-gray-500">
                                    Noch keine Änderungen vorhanden
                                </div>
                            </div>
                        </div>
                    </div>
                    <button
                        class="btn-stage-icon-plain"
                        type="button"
                        :title="$t('Reload')"
                        :aria-label="$t('Reload')"
                        @click="getDocumentation"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrows-repeat"></svg-vue>
                    </button>
                </div>
            </div>
        </div>

        <div class="stage-page-gutters stage-page-fill pt-3 h-0">
            <div class="w-full h-full bg-white shadow rounded-md border border-ultralight overflow-hidden">
                <div class="flex h-full min-h-0 items-stretch">
                    <div ref="contentScrollContainer" class="flex-auto min-w-0 overflow-y-auto">
                        <div class="sticky top-0 z-10 bg-surface-raised border-b border-ultralight">
                            <div class="flex items-stretch min-h-12">
                                <div class="w-13 flex-shrink-0 border-r border-ultralight flex items-center justify-center text-gray-400">
                                    <svg-vue class="h-4 w-4" icon="font-awesome.regular.book-open-cover"></svg-vue>
                                </div>
                                <div class="flex-1 min-w-0 border-r border-ultralight px-4">
                                    <div class="flex h-full items-center">
                                        <span class="block truncate text-sm font-medium text-gray-700">
                                            {{ documentation.title || $t('Documentation') }}
                                        </span>
                                    </div>
                                </div>
                                <div class="w-14 flex-shrink-0 lg:flex-1 lg:min-w-0 lg:min-w-[250px]">
                                    <div class="relative h-full">
                                        <div class="hidden h-full items-center pl-4 pr-9 lg:flex">
                                            <span class="block truncate text-sm leading-5 font-medium text-gray-700">
                                                {{ $t('Jump to section') }}
                                            </span>
                                        </div>
                                        <div class="pointer-events-none absolute inset-y-0 right-0 z-10 flex items-center pr-4 text-gray-400">
                                            <svg-vue class="h-[0.875rem] w-[0.875rem]" icon="font-awesome.regular.angle-down"></svg-vue>
                                        </div>
                                        <select
                                            v-model="selectedMenuAnchorId"
                                            class="block form-select absolute inset-0 z-20 w-full h-full border-0 rounded-none bg-transparent pl-4 pr-9 py-0 text-sm leading-5 font-medium text-gray-700 cursor-pointer opacity-0"
                                            @change="onMenuAnchorChange"
                                        >
                                            <option disabled value="">{{ $t('Jump to section') }}</option>
                                            <option
                                                v-for="item in menuAnchors"
                                                :key="'doc-menu-anchor-' + item.id"
                                                :value="item.id"
                                            >
                                                {{ item.label }}
                                            </option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="px-8 py-8">
                            <div class="ticket-reply-body text-gray-700" v-html="documentation.body"></div>
                            <template v-if="documentation.attachments.length > 0">
                                <div class="mt-5 flex flex-wrap items-start gap-2">
                                    <template v-for="attachment in documentation.attachments" :key="attachment.id || attachment.uuid">
                                        <attachment :details="attachment" :remove-button="false"/>
                                    </template>
                                </div>
                            </template>
                        </div>
                    </div>

                    <aside :style="{flex: '0 0 300px', width: '300px'}" class="documentation-sidebar hidden xl:block self-stretch bg-white border-l border-bordergray-100 overflow-y-auto">
                        <div class="p-6">
                            <div class="flex items-center">
                                <img
                                    :alt="$t('Avatar')"
                                    :src="documentationAuthorAvatar"
                                    class="h-14 w-14 rounded-full object-cover flex-shrink-0"
                                />
                                <div class="ml-4 min-w-0 flex-1">
                                    <div class="mb-2 font-semibold text-base text-gray-800 truncate">{{ documentation.author || '-' }}</div>
                                    <div class="mb-1 text-sm leading-4 text-gray-500 truncate">
                                        {{ documentation.author_email || '-' }}
                                    </div>
                                    <div v-if="documentation.author_phone" class="text-sm leading-4 text-gray-500 truncate">
                                        {{ documentation.author_phone }}
                                    </div>
                                </div>
                            </div>

                            <div class="my-6 border-t border-bordergray-100"></div>

                            <div v-if="documentationInternalRecipients.length > 0">
                                <div class="text-xs tracking-wide text-gray-500 mb-2">{{ $t('Interne Freigaben') }}</div>
                                <div class="space-y-4">
                                    <template v-for="person in documentationInternalRecipients" :key="'documentation-internal-' + person.id">
                                        <div class="flex items-center">
                                            <img
                                                :alt="$t('Avatar')"
                                                :src="person.avatar !== 'gravatar' ? person.avatar : person.gravatar"
                                                class="h-7 w-7 rounded-full object-cover mr-3 flex-shrink-0"
                                            />
                                            <div class="min-w-0 flex-1">
                                                <div class="text-sm text-gray-900 truncate w-full">{{ person.name }}</div>
                                                <div class="text-sm leading-4 text-gray-500 truncate w-full">{{ person.email || '-' }}</div>
                                            </div>
                                        </div>
                                    </template>
                                </div>
                            </div>

                            <div v-if="documentationCustomerRecipients.length > 0" class="mt-6">
                                <div class="text-xs tracking-wide text-gray-500 mb-2">{{ $t('Geteilt') }}</div>
                                <div class="space-y-4">
                                    <template v-for="person in documentationCustomerRecipients" :key="'documentation-customer-' + person.id">
                                        <div class="flex items-center">
                                            <img
                                                :alt="$t('Avatar')"
                                                :src="person.avatar !== 'gravatar' ? person.avatar : person.gravatar"
                                                class="h-7 w-7 rounded-full object-cover mr-3 flex-shrink-0"
                                            />
                                            <div class="min-w-0 flex-1">
                                                <div class="text-sm text-gray-900 truncate w-full">{{ person.name }}</div>
                                                <div class="text-sm leading-4 text-gray-500 truncate w-full">{{ person.email || '-' }}</div>
                                            </div>
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {mixin as clickaway} from "vue-clickaway";
import {resolveReturnToPath} from "@/utils/list-route-state";

export default {
    name: "documentation-customer-manage",
    mixins: [clickaway],
    metaInfo() {
        return {
            title: "Dokumentation"
        }
    },
    data() {
        return {
            loading: true,
            historyPopoverOpen: false,
            selectedMenuAnchorId: '',
            documentation: {
                title: null,
                body: null,
                author: null,
                author_email: null,
                author_phone: null,
                author_company: null,
                author_avatar: null,
                readers: [],
                customers: [],
                guests: [],
                attachments: [],
                change_history: [],
                updated_at: null,
            },
        };
    },
    computed: {
        documentationListPath() {
            return resolveReturnToPath(this.$route.query || {}, '/documentations/list');
        },
        menuAnchors() {
            const body = this.documentation && this.documentation.body ? String(this.documentation.body) : '';
            if (!body) {
                return [];
            }

            const parser = new DOMParser();
            const doc = parser.parseFromString(body, 'text/html');
            const nodes = Array.from(doc.querySelectorAll('[id^="doc-menu-"], h1[id], h2[id], h3[id]'));
            const anchors = [];
            const seenIds = new Set();

            nodes.forEach((node) => {
                const rawId = (node.getAttribute('id') || '').trim();
                if (!rawId || seenIds.has(rawId)) {
                    return;
                }

                const rawLabel = (node.textContent || '').trim();
                if (!rawLabel) {
                    return;
                }

                if (!rawId.startsWith('doc-menu-') && !['H1', 'H2', 'H3'].includes(node.tagName)) {
                    return;
                }

                seenIds.add(rawId);
                anchors.push({
                    id: rawId,
                    label: rawLabel,
                });
            });

            return anchors;
        },
        documentationAuthorAvatar() {
            return this.documentation && this.documentation.author_avatar ? this.documentation.author_avatar : '';
        },
        documentationInternalRecipients() {
            const recipients = [];
            const seenIds = new Set();
            const authorName = this.documentation && this.documentation.author
                ? String(this.documentation.author)
                : '';
            const addRecipient = (person) => {
                const id = Number(person && person.id ? person.id : 0);
                if (!id || seenIds.has(id)) {
                    return;
                }
                const name = person && person.name ? String(person.name) : '';
                if (name && authorName && name === authorName) {
                    return;
                }
                seenIds.add(id);
                recipients.push({
                    id,
                    name: name || '-',
                    email: person && person.email ? String(person.email) : '',
                    avatar: person && person.avatar ? person.avatar : '',
                    gravatar: person && person.gravatar ? person.gravatar : '',
                });
            };

            const readers = Array.isArray(this.documentation && this.documentation.readers)
                ? this.documentation.readers
                : [];
            readers.forEach(addRecipient);

            return recipients;
        },
        documentationCustomerRecipients() {
            const recipients = [];
            const seenIds = new Set();
            const authorName = this.documentation && this.documentation.author
                ? String(this.documentation.author)
                : '';
            const addRecipient = (person) => {
                const id = Number(person && person.id ? person.id : 0);
                if (!id || seenIds.has(id)) {
                    return;
                }
                const name = person && person.name ? String(person.name) : '';
                if (name && authorName && name === authorName) {
                    return;
                }
                seenIds.add(id);
                recipients.push({
                    id,
                    name: name || '-',
                    email: person && person.email ? String(person.email) : '',
                    avatar: person && person.avatar ? person.avatar : '',
                    gravatar: person && person.gravatar ? person.gravatar : '',
                });
            };

            const customers = Array.isArray(this.documentation && this.documentation.customers)
                ? this.documentation.customers
                : [];
            const guests = Array.isArray(this.documentation && this.documentation.guests)
                ? this.documentation.guests
                : [];
            [...customers, ...guests].forEach(addRecipient);

            return recipients;
        },
    },
    mounted() {
        this.getDocumentation();
    },
    filters: {
        momentFormatDateTimeAgo: function (value) {
            return moment(value).locale(window.app.app_date_locale).fromNow();
        },
    },
    methods: {
        enhanceRenderedBody() {
            this.$nextTick(function () {
                if (typeof window.__enhanceCodeBlocks === 'function') {
                    window.__enhanceCodeBlocks(this.$el || document);
                }
            });
        },
        closeActionDropdowns() {
            this.historyPopoverOpen = false;
        },
        toggleHistoryPopover() {
            this.historyPopoverOpen = !this.historyPopoverOpen;
        },
        formatDateTime(value) {
            if (!value) {
                return '-';
            }

            return moment(value).format('DD.MM.YYYY HH:mm');
        },
        onMenuAnchorChange() {
            if (!this.selectedMenuAnchorId) {
                return;
            }
            this.scrollToMenuAnchor(this.selectedMenuAnchorId);
        },
        scrollToMenuAnchor(anchorId) {
            this.$nextTick(function () {
                const element = document.getElementById(anchorId);
                if (!element) {
                    return;
                }

                const container = this.$refs.contentScrollContainer;
                if (!container) {
                    element.scrollIntoView({behavior: 'smooth', block: 'start'});
                    return;
                }

                const containerRect = container.getBoundingClientRect();
                const elementRect = element.getBoundingClientRect();
                const stickyHeaderOffset = 80;
                const nextTop = container.scrollTop + (elementRect.top - containerRect.top) - stickyHeaderOffset;

                container.scrollTo({
                    top: Math.max(0, nextTop),
                    behavior: 'smooth'
                });
            });
        },
        getDocumentation() {
            const self = this;
            self.loading = true;
            axios.get('api/documentations/' + self.$route.params.id).then(function (response) {
                self.documentation = response.data;
                self.selectedMenuAnchorId = '';
                self.loading = false;
                self.enhanceRenderedBody();
            }).catch(function () {
                self.loading = false;
                self.$router.push('/documentations/list');
            });
        },
    }
}
</script>

<style lang="scss">
.ticket-reply-body .doc-menu-anchor-chip.doc-menu-anchor-heading-h1 {
    font-size: 1.5rem;
    line-height: 2rem;
    font-weight: 600;
}

.ticket-reply-body .doc-menu-anchor-chip.doc-menu-anchor-heading-h2 {
    font-size: 1.25rem;
    line-height: 1.75rem;
    font-weight: 600;
}

.ticket-reply-body h1 {
    font-size: 1.5rem;
    line-height: 2rem;
    font-weight: 600;
    margin: 0 0 0.75rem 0;
}

.ticket-reply-body h2 {
    font-size: 1.25rem;
    line-height: 1.75rem;
    font-weight: 600;
    margin: 0 0 0.625rem 0;
}
</style>
